/**
 * Memos — reads + writes through the Hono backend
 * (`apps/api/src/routes/platform/memos.ts`, `/api/v1/memos/orgs/:orgId/memos…`, gated
 * by the `memos` feature flag). Every hook reads the org from
 * `useOrganization()`, like `useBusinessIndex()`.
 *
 * ── Liveness contract ───────────────────────────────────────────────────────
 * Every memo cache EXCEPT the audio URL nests under `['memos', orgId]`:
 *   index   `['memos', orgId, 'index', {}]`      (indexQueryOptions)
 *   detail  `['memos', orgId, 'detail', id]`
 *   status  `['memos', orgId, 'status', id]`     (the poller)
 * Every mutation's `onSuccess` invalidates that literal two-slot prefix, and
 * the poller does too when a status turns terminal — one call refreshes the
 * grid, the open detail page and the poller (which resumes polling after a
 * retranscribe/resummarize flips the status back to `pending`).
 *
 * The signed audio URL is keyed `['memo-audio-url', orgId, id]` — deliberately
 * OUTSIDE `['memos']`, so a speaker assignment or a finished transcription
 * never swaps the player's `src` mid-playback. The literal keys are pinned in
 * `useMemos.test.ts`; keep them.
 *
 * Accepted staleness (60s index `staleTime`): a project / business / person
 * rename and the summary's description auto-fill reach the grid on the next
 * refetch, not instantly.
 *
 * Status columns are SERVER-OWNED (Clay's ruling 1, 2026-09-24): the browser
 * never writes `transcription_status` / `summary_status`. The transcribe and
 * summarize routes CAS the status and invoke the edge function themselves; a
 * 409 means a run is already in flight.
 *
 * Errors: `onError` TOASTS ONLY. The app-level `MutationCache.onError` already
 * captures every mutation failure to Sentry (docs/OBSERVABILITY.md) — a second
 * `captureException` would double-report. The one direct capture is the
 * best-effort orphan cleanup in `useCreateMemo`, which is not the mutation's
 * own error.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MemoAcceptedResponse,
  MemoAudioContentType,
  MemoAudioUrlResponse,
  MemoDeleteResponse,
  MemoDetailWire,
  MemoIndexRowWire,
  MemoResponse,
  MemoStatusResponse,
  MemoStatusWire,
  UpdateMemoInput,
} from '@releve/shared';
// eslint-disable-next-line no-restricted-imports -- Storage upload stays browser-direct (BACKEND_API.md: uploads undecided)
import { supabase } from '@/integrations/supabase/client';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import { useOrganization } from '@/contexts/OrganizationContext';
import { authedDelete, authedGet, authedPatch, authedPost } from '@/lib/api';
import { toast } from '@/hooks/useToast';
import { captureException } from '@/lib/sentry';
import i18n from '@/i18n';

/** How long a status may sit in `pending`/`processing` before the UI offers a retry. */
export const MEMO_STUCK_AFTER_MS = 15 * 60 * 1000;

const POLL_INTERVAL_MS = 3000;

const memosBase = (orgId: string | undefined) => `/api/v1/memos/orgs/${orgId}/memos`;

const requireOrgId = (orgId: string | undefined): string => {
  if (!orgId) throw new Error('No organization selected');
  return orgId;
};

/** Failure toast: one generic title, the server's message as the description. */
const toastMemoError = (error: unknown) => {
  const description = error instanceof Error && error.message ? error.message : undefined;
  toast.error(i18n.t('memos:toasts.error'), description ? { description } : undefined);
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * The COMPLETE slim memo index for the selected org (the `memo_index` view,
 * paged past the 1,000-row cap by the API). No params and no polling: the
 * filter document does all filtering in the browser, and in-flight statuses
 * refresh through the poller's terminal invalidation.
 *
 * THE ENTITY PREFIX IS `'memos'` — the prefix every writer in this file
 * invalidates. Renaming it silently freezes the grid after a write.
 */
export function useMemoIndex() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery(
    indexQueryOptions<MemoIndexRowWire>({
      entity: 'memos',
      organizationId: orgId,
      path: `${memosBase(orgId)}/index`,
    }),
  );
}

/** One memo with its transcript, speakers and summary. */
export function useMemoDetail(memoId?: string) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery({
    queryKey: ['memos', orgId, 'detail', memoId],
    queryFn: async (): Promise<MemoDetailWire> => {
      const res = await authedGet<MemoResponse>(`${memosBase(orgId)}/${memoId}`);
      return res.memo;
    },
    enabled: !!orgId && !!memoId,
  });
}

/**
 * A 1-hour signed URL for the memo's audio (`null` until it resolves).
 *
 * Keyed OUTSIDE `['memos']` (see the file header) so detail invalidations
 * never change the player `src`; refreshed after 50 minutes, before the
 * signature expires, and never on window focus (a new URL would reload the
 * player).
 */
export function useMemoAudioUrl(memoId: string | undefined, enabled: boolean): string | null {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const { data } = useQuery({
    queryKey: ['memo-audio-url', orgId, memoId],
    queryFn: async (): Promise<string> => {
      const res = await authedGet<MemoAudioUrlResponse>(`${memosBase(orgId)}/${memoId}/audio-url`);
      return res.url;
    },
    enabled: enabled && !!orgId && !!memoId,
    staleTime: 50 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  return data ?? null;
}

const IN_FLIGHT = new Set(['pending', 'processing']);
const isInFlight = (s: MemoStatusWire | null | undefined): boolean =>
  !!s && (IN_FLIGHT.has(s.transcription_status ?? '') || IN_FLIGHT.has(s.summary_status ?? ''));

/**
 * Stuck = still in flight more than 15 minutes after the row last changed.
 * `now` is the fetch time (`dataUpdatedAt`), so it advances with every poll
 * even when the payload is unchanged.
 */
const isStuckAt = (s: MemoStatusWire | null | undefined, now: number): boolean => {
  if (!isInFlight(s) || !s?.updated_at) return false;
  const changed = Date.parse(s.updated_at);
  return Number.isFinite(changed) && now - changed > MEMO_STUCK_AFTER_MS;
};

/**
 * Polls `GET …/status` every 3s while a transcription or summary is in flight,
 * and stops once it is done — or stuck (`isStuck`, in flight for over 15
 * minutes, which is when the detail page offers "Retry").
 *
 * On a transition to a terminal status it toasts and invalidates
 * `['memos', orgId]`, refreshing the detail, the index and itself.
 *
 * Caveat: the 15-minute clock is `memos.updated_at`, so any write to the row
 * while it is processing (a PATCH of the title, a speaker assignment) resets it.
 */
export function useMemoStatusPoller(memoId?: string) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();
  const prevRef = useRef<MemoStatusWire | null>(null);

  const query = useQuery({
    queryKey: ['memos', orgId, 'status', memoId],
    queryFn: async (): Promise<MemoStatusWire> => {
      const res = await authedGet<MemoStatusResponse>(`${memosBase(orgId)}/${memoId}/status`);
      return res.status;
    },
    enabled: !!orgId && !!memoId,
    refetchInterval: (q) =>
      isInFlight(q.state.data) && !isStuckAt(q.state.data, q.state.dataUpdatedAt)
        ? POLL_INTERVAL_MS
        : false,
  });

  const status = query.data ?? null;

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = status;
    if (!status || !prev) return;

    const transcriptionDone =
      IN_FLIGHT.has(prev.transcription_status ?? '') &&
      !IN_FLIGHT.has(status.transcription_status ?? '');
    const summaryDone =
      IN_FLIGHT.has(prev.summary_status ?? '') && !IN_FLIGHT.has(status.summary_status ?? '');
    if (!transcriptionDone && !summaryDone) return;

    if (transcriptionDone) {
      if (status.transcription_status === 'completed') {
        toast.success(i18n.t('memos:poller.transcriptionCompleted'));
      } else if (status.transcription_status === 'failed') {
        toast.error(i18n.t('memos:poller.transcriptionFailed'));
      }
    }
    if (summaryDone) {
      if (status.summary_status === 'completed') {
        toast.success(i18n.t('memos:poller.summaryCompleted'));
      } else if (status.summary_status === 'failed') {
        toast.warning(i18n.t('memos:poller.summaryFailed'));
      }
    }
    void queryClient.invalidateQueries({ queryKey: ['memos', orgId] });
  }, [status, orgId, queryClient]);

  return { status, isStuck: isStuckAt(status, query.dataUpdatedAt) };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface CreateMemoVars {
  title: string;
  description?: string | null;
  project_id?: string | null;
  business_id?: string | null;
  /** A `people` id (FK `memos_person_id_fkey`, ON DELETE SET NULL). */
  person_id?: string | null;
  audioBlob: Blob;
  durationSeconds: number;
  /** The recorder's mime type; codec parameters are stripped before upload. */
  contentType: string;
  enableEntityDetection: boolean;
}

/** `audio/webm;codecs=opus` → `audio/webm`; anything that is not mp4 is webm. */
const normalizeAudioContentType = (contentType: string): MemoAudioContentType =>
  contentType.split(';')[0].trim().toLowerCase() === 'audio/mp4' ? 'audio/mp4' : 'audio/webm';

/**
 * Upload the recording, then create the memo. The upload is browser-direct
 * (the sanctioned storage carve-out) to `<orgId>/memos/<uuid>.<ext>` — the only
 * path shape the API accepts. If the POST fails, the object is removed
 * best-effort so an abandoned upload does not linger. The API starts the
 * transcription itself (status defaults to `pending`).
 */
export function useCreateMemo() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: CreateMemoVars): Promise<MemoDetailWire> => {
      const org = requireOrgId(orgId);
      const contentType = normalizeAudioContentType(vars.contentType);
      const ext = contentType === 'audio/mp4' ? 'mp4' : 'webm';
      const path = `${org}/memos/${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('files')
        .upload(path, vars.audioBlob, { contentType, upsert: false });
      if (uploadError) throw uploadError;

      try {
        const res = await authedPost<MemoResponse>(memosBase(org), {
          title: vars.title,
          description: vars.description || null,
          project_id: vars.project_id || null,
          business_id: vars.business_id || null,
          person_id: vars.person_id || null,
          audio_file_path: path,
          audio_duration_seconds: vars.durationSeconds,
          audio_content_type: contentType,
          enable_entity_detection: vars.enableEntityDetection,
        });
        return res.memo;
      } catch (error) {
        try {
          const { error: removeError } = await supabase.storage.from('files').remove([path]);
          if (removeError) throw removeError;
        } catch (cleanupError) {
          captureException(cleanupError, { context: 'memo orphan audio cleanup', path });
        }
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['memos', orgId] });
      toast.success(i18n.t('memos:toasts.created'));
    },
    onError: toastMemoError,
  });
}

export type UpdateMemoVars = { id: string } & UpdateMemoInput;

/** Header fields, links and speaker assignments. Creator or org admin only (403 otherwise). */
export function useUpdateMemo() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateMemoVars): Promise<MemoDetailWire> => {
      const res = await authedPatch<MemoResponse>(`${memosBase(requireOrgId(orgId))}/${id}`, updates);
      return res.memo;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['memos', orgId] });
      toast.success(i18n.t('memos:toasts.updated'));
    },
    onError: toastMemoError,
  });
}

/**
 * Delete a memo; the API removes its audio object after the row is gone.
 * The deleted memo's own detail/status/audio caches are dropped first so the
 * prefix invalidation does not refetch a 404.
 */
export function useDeleteMemo() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }): Promise<MemoDeleteResponse> =>
      authedDelete<MemoDeleteResponse>(`${memosBase(requireOrgId(orgId))}/${id}`),
    onSuccess: async (_, { id }) => {
      queryClient.removeQueries({ queryKey: ['memos', orgId, 'detail', id] });
      queryClient.removeQueries({ queryKey: ['memos', orgId, 'status', id] });
      queryClient.removeQueries({ queryKey: ['memo-audio-url', orgId, id] });
      await queryClient.invalidateQueries({ queryKey: ['memos', orgId] });
      toast.success(i18n.t('memos:toasts.deleted'));
    },
    onError: toastMemoError,
  });
}

/** Start (or retry) a transcription. 202 = accepted; 409 = one is already in flight. */
export function useRetranscribeMemo() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memoId,
      enableEntityDetection,
    }: {
      memoId: string;
      enableEntityDetection: boolean;
    }): Promise<MemoAcceptedResponse> =>
      authedPost<MemoAcceptedResponse>(`${memosBase(requireOrgId(orgId))}/${memoId}/transcribe`, {
        enable_entity_detection: enableEntityDetection,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['memos', orgId] });
      toast.info(i18n.t('memos:toasts.retranscribeStarted'));
    },
    onError: toastMemoError,
  });
}

/** Start (or retry) the AI summary. Needs a completed transcription and the `memo_ai_summarization` flag. */
export function useResummarizeMemo() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memoId }: { memoId: string }): Promise<MemoAcceptedResponse> =>
      authedPost<MemoAcceptedResponse>(`${memosBase(requireOrgId(orgId))}/${memoId}/summarize`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['memos', orgId] });
      toast.info(i18n.t('memos:toasts.resummarizeStarted'));
    },
    onError: toastMemoError,
  });
}
